import React, { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { API } from "aws-amplify";
import {
  Container,
  Grid,
  TextField,
  Paper,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemSecondaryAction,
  ListItemText
} from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";

const EmailAlerts = props => {
  // var for easier access to all alerts
  const allAlerts = props.authProps.tenantConfig.emailAlerts;

  // convert object to an arry of objects and filter by tag
  const getSevereWeatherAlerts = () => {
    return Object.entries(allAlerts)
      .map(([k, v]) => ({
        newsletterId: k,
        display_name: v.display_name,
        tags: v.tags[0],
        status: "inactive"
      }))
      .filter(function(el) {
        return el.tags === "severe-weather";
      })
      .sort((a, b) => (a.display_name > b.display_name ? 1 : -1));
  };

  // convert object to an arry of objects and filter by tag
  const getClosingsAlerts = () => {
    return Object.entries(allAlerts)
      .map(([k, v]) => ({
        newsletterId: k,
        display_name: v.display_name,
        tags: v.tags[0],
        status: "inactive"
      }))
      .filter(function(el) {
        return el.tags === "closings";
      })
      .sort((a, b) => (a.display_name > b.display_name ? 1 : -1));
  };

  useEffect(() => {
    if (!userSubscriptionsLoaded) {
      if (props.authProps && props.authProps.user) {
        getProfile(props.authProps.user.attributes.email);
        setUserEmail(props.authProps.user.attributes.email);
      }
    }
  }, [props.authProps]); // eslint-disable-line react-hooks/exhaustive-deps

  // get the user's subscriptions
  const getProfile = async email => {
    const profile = await API.get("extendedProfiles", `/${email}`, {
      queryStringParameters: {
        emailAlertSubscriptionsOnly: true
      }
    });
    setuserSubscriptionsLoaded(true);
    updateAlerts(profile.subscriptions);
    // leaving this for QA
    console.log("profile: ", profile);
  };

  // update the alerts list of already subscribed
  const updateAlerts = subscriptions => {
    let emailAlertSubscriptions = [];

    subscriptions.forEach(function(sub) {
      // since all the suncsriptions are in one list,
      // we have to pull out the ones that have a key of emailalertId
      if ("emailalertId" in sub) {
        emailAlertSubscriptions.push(sub);
      }
    });
    setWeatherAlertsSelected([]);
    setClosingsAlertsSelected([]);
    emailAlertSubscriptions.forEach(function(item) {
      const severeWetherAlertToChange = severeWeatherAlerts.find(
        alert => alert.newsletterId === item.emailalertId
      );

      const closingsAlertToChange = closingsAlerts.find(
        alert => alert.newsletterId === item.emailalertId
      );

      // now that we have the email alerts, we have to set the alerts
      // selected with the correct status
      if (severeWetherAlertToChange) {
        severeWetherAlertToChange.status = item.status;

        setWeatherAlertsSelected(state =>
          state.concat({
            display_name: severeWetherAlertToChange.display_name,
            newsletterId: severeWetherAlertToChange.newsletterId,
            tags: severeWetherAlertToChange.tags
          })
        );
      } else if (closingsAlertToChange) {
        closingsAlertToChange.status = item.status;

        setClosingsAlertsSelected(state =>
          state.concat({
            display_name: closingsAlertToChange.display_name,
            newsletterId: closingsAlertToChange.newsletterId,
            tags: closingsAlertToChange.tags
          })
        );
      }
    });
  };

  // subscribe and unsubsribe to weather alerts
  function alertSubscribe(e, values, type, process) {
    if (values) {
      postSubscription(
        userEmail,
        values.newsletterId,
        values.status === "inactive" ? "active" : "inactive"
      );

      if (type === "weather") {
        const severeWetherAlertToChange = severeWeatherAlerts.find(
          alert => alert.newsletterId === values.newsletterId
        );

        if (process === "remove") {
          // sets the status of the severeWeatherAlerts item to inactive to show as disabled
          severeWetherAlertToChange.status = "inactive";
          setSevereWeatherAlerts(severeWeatherAlerts);

          // sets the list of selected to the correct ones
          const filteredItems = weatherAlertsSelected.filter(
            item => item.newsletterId !== severeWetherAlertToChange.newsletterId
          );
          setWeatherAlertsSelected(filteredItems);
        } else {
          // sets the status of the severeWeatherAlerts item to active to show as enabled
          severeWetherAlertToChange.status = "active";
          setSevereWeatherAlerts(severeWeatherAlerts);

          // sets the list of selected to the correct ones
          setWeatherAlertsSelected(state =>
            state.concat({
              display_name: values.display_name,
              newsletterId: values.newsletterId,
              tags: values.tags
            })
          );
        }
      } else {
        const closingsAlertToChange = closingsAlerts.find(
          alert => alert.newsletterId === values.newsletterId
        );

        if (process === "remove") {
          // sets the status of the severeWeatherAlerts item to inactive to show as disabled
          closingsAlertToChange.status = "inactive";
          setClosingsAlerts(closingsAlerts);

          const filteredItems = closingsAlertsSelected.filter(
            item => item.newsletterId !== closingsAlertToChange.newsletterId
          );

          setClosingsAlertsSelected(filteredItems);
        } else {
          // sets the status of the severeWeatherAlerts item to inactive to show as disabled
          closingsAlertToChange.status = "active";
          setClosingsAlerts(closingsAlerts);

          setClosingsAlertsSelected(state =>
            state.concat({
              display_name: values.display_name,
              newsletterId: values.newsletterId,
              tags: values.tags
            })
          );
        }
      }
    }
  }

  // post to api
  const postSubscription = async (email, newsletterId, status) => {
    const response = await API.post("userEmailAlerts", ``, {
      queryStringParameters: {
        email: email
      },
      body: {
        emailAlertId: newsletterId,
        status: status
      }
    });

    // leaving this for QA
    console.log("response: ", response);
  };

  let [userSubscriptionsLoaded, setuserSubscriptionsLoaded] = useState("");
  let [severeWeatherAlerts, setSevereWeatherAlerts] = useState(
    getSevereWeatherAlerts
  );
  let [closingsAlerts, setClosingsAlerts] = useState(getClosingsAlerts);
  let [weatherAlertsSelected, setWeatherAlertsSelected] = useState([]);
  let [closingsAlertsSelected, setClosingsAlertsSelected] = useState([]);

  const [userEmail, setUserEmail] = useState("");

  // styles
  const useStyles = makeStyles(theme => ({
    paper: {
      ...theme.paper
    },
    autocomplete: {
      width: "auto",
      paddingTop: theme.spacing(2)
    }
  }));

  const classes = useStyles();

  if (props.authProps.authState) {
    if (props.authProps.isAuthenticated && props.authProps.user) {
      let content = (
        <Container maxWidth="lg">
          <Typography variant="h2">Email Alerts</Typography>
          <Grid container spacing={2}>
            <Grid item lg={6} md={12}>
              <Paper className={classes.paper}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h3">
                      Severe Weather Email Alerts
                    </Typography>
                    <Typography variant="body2">
                      Type in the Locations you want to track
                    </Typography>
                    <Autocomplete
                      id="severe-weather-alerts"
                      className={classes.autocomplete}
                      options={severeWeatherAlerts}
                      getOptionDisabled={option => option.status === "active"}
                      getOptionLabel={alert => alert.display_name}
                      onChange={(e, alert) => {
                        alertSubscribe(e, alert, "weather", "add");
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Severe Weather Alerts"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1">Locations Selected</Typography>
                    <List dense>
                      {weatherAlertsSelected.map((alert, i) => (
                        <ListItem key={i}>
                          <ListItemText>{alert.display_name}</ListItemText>
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={e =>
                                alertSubscribe(e, alert, "weather", "remove")
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item lg={6} md={12}>
              <Paper className={classes.paper}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h3">Closings Email Alerts</Typography>
                    <Typography variant="body2">
                      Type in the names of the business you want to track
                    </Typography>
                    <Autocomplete
                      id="closings-alerts"
                      className={classes.autocomplete}
                      options={closingsAlerts}
                      getOptionDisabled={option => option.status === "active"}
                      getOptionLabel={option => option.display_name}
                      onChange={(e, alert) => {
                        alertSubscribe(e, alert, "closing", "add");
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Closings Alerts"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1">Locations Selected</Typography>
                    <List dense>
                      {closingsAlertsSelected.map((alert, i) => (
                        <ListItem key={i}>
                          <ListItemText>{alert.display_name}</ListItemText>
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={e =>
                                alertSubscribe(e, alert, "closing", "remove")
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      );

      return <div className="box cta">{content}</div>;
    } else {
      return <Redirect to="/sign-in" />;
    }
  } else {
    return <span className="tag is-primary">Loading ...</span>;
  }
};

export default EmailAlerts;
